"use client";

import { LogoWordmark } from "@/components/ui/logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useEarlyAccess } from "@/components/early-access";
import { DOCS_URL } from "@/components/navbar";

const CONTACT_EMAIL = "info@paranine.com";

// Root-relative rather than bare hashes: the footer is shared with every
// marketing route, where a bare `#models` would scroll to nothing.
const COLUMNS: Array<{
  title: string;
  links: Array<{ label: string; href: string; external?: boolean }>;
}> = [
  {
    title: "Product",
    links: [
      { label: "Developers", href: "/developers" },
      { label: "Enterprise", href: "/enterprise" },
      { label: "P/9 Gateway", href: "/#gateway" },
      // Was "/#models", an anchor to the home page band, while the navbar
      // sent an identically labelled link to the catalogue page. Two links
      // reading "Models" going to two places is what Lighthouse flags as
      // "identical links have the same purpose", and it is a genuine
      // annoyance rather than a technicality: a reader cannot tell which one
      // they are about to get. The catalogue is the better destination, so
      // both now point at it.
      { label: "Models", href: "/models" },
      { label: "PK Endpoint", href: "/pk-endpoint" },
      { label: "Pricing", href: "/pricing" },
      { label: "Dedicated inference", href: "/dedicated" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "About", href: "/about" },
      { label: "Documentation", href: DOCS_URL, external: true },
      { label: "Blog", href: "/blog" },
      // News and the press kit were reachable only from the navbar's Media
      // menu, which is a hover target on desktop and two taps on a phone.
      // Every page carries this footer, so these two lines are what actually
      // links the announcement from across the site.
      { label: "News & Announcements", href: "/media/news" },
      { label: "Press kit", href: "/media" },
      { label: "Careers", href: "/careers" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Forward Deployed Engineering Services", href: "/engineers" },
      { label: "Hire an FDE", href: "/engineers#talk" },
      { label: "Email us", href: `mailto:${CONTACT_EMAIL}` },
    ],
  },
];

const LEGAL: Array<{ label: string; href: string }> = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Refunds", href: "/refunds" },
];

export function Footer() {
  const { open } = useEarlyAccess();

  return (
    <footer className="relative">
      <div className="mx-auto max-w-[90rem] px-5 pt-16 sm:px-8 sm:pt-20">
        <div className="grid gap-12 pb-14 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          {/* brand */}
          <div className="max-w-xs md:col-span-2 lg:col-span-1">
            <span className="inline-flex items-center text-foreground">
              <LogoWordmark className="h-9 w-40" />
              <span className="sr-only">paranine</span>
            </span>
            <p className="mt-5 font-sans text-sm leading-relaxed text-muted">
              Pakistan&rsquo;s sovereign AI gateway. One OpenAI-compatible API to
              leading open-source models, run and billed in rupees.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <ThemeToggle />
              <span className="font-sans text-xs text-muted-2">
                From Pakistan, for the AMEA region
              </span>
            </div>
          </div>

          {/* link columns */}
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="font-sans text-xs uppercase tracking-[0.18em] text-muted-2">
                {col.title}
              </h3>
              <ul className="mt-5 flex flex-col gap-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noopener noreferrer" : undefined}
                      className="font-sans text-sm text-foreground/80 transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
                {col.title === "Support" && (
                  <li>
                    <button
                      onClick={open}
                      className="font-sans text-sm text-foreground/80 transition-colors hover:text-foreground"
                    >
                      Get early access
                    </button>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-4 py-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1.5">
            <p className="font-sans text-xs text-muted-2">
              © {2026} paranine. All rights reserved.
            </p>
            <p className="font-sans text-xs text-muted-2">
              Sovereign gateway. NPU-native inference. Billed in rupees.
            </p>
          </div>

          {/* The legal set, added 2026-09-10. It sits in the bottom bar rather
              than in a fifth link column on purpose: these are documents a
              reader looks for in a known place, not things we are inviting
              anyone to browse, and a fifth column would have changed the grid
              above. Two forms already promised "our privacy policy" before
              this existed, which is the exposure these pages close. */}
          <nav aria-label="Legal">
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {LEGAL.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="font-sans text-xs text-muted-2 transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
}
